import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Ellipsis component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconEllipsis extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.8031 14.3411C21.0961 14.3411 22.1442 13.293 22.1442 12C22.1442 10.707 21.0961 9.65881 19.8031 9.65881C18.5101 9.65881 17.4619 10.707 17.4619 12C17.4619 13.293 18.5101 14.3411 19.8031 14.3411Z"
          />
          <path
            d="M11.9994 14.3411C13.2923 14.3411 14.3405 13.293 14.3405 12C14.3405 10.707 13.2923 9.65881 11.9994 9.65881C10.7064 9.65881 9.6582 10.707 9.6582 12C9.6582 13.293 10.7064 14.3411 11.9994 14.3411Z"
          />
          <path
            d="M4.19565 14.3411C5.48863 14.3411 6.5368 13.293 6.5368 12C6.5368 10.707 5.48863 9.65881 4.19565 9.65881C2.90266 9.65881 1.85449 10.707 1.85449 12C1.85449 13.293 2.90266 14.3411 4.19565 14.3411Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-ellipsis') === undefined) {
  customElements.define('cat-icon-ellipsis', BlocksIconEllipsis);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-ellipsis': BlocksIconEllipsis;
  }
}
